import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Save, RefreshCw, Folder, Search, ArrowUp, ArrowDown } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = 'w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-3 py-2.5 text-[14px] outline-none focus:border-neutral-400 dark:focus:border-neutral-600';

const emptyForm = { id: '', display: '', label: '', sort_order: '' };

const slugify = (s) => (s || '')
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9\s-]/g, '')
  .replace(/\s+/g, '-')
  .replace(/-+/g, '-')
  .slice(0, 32);

const FormRow = ({ label, children, hint }) => (
  <label className="block">
    <div className="text-[11px] tracking-widest uppercase text-neutral-500 mb-1.5">{label}</div>
    {children}
    {hint && <div className="text-[11px] text-neutral-500 mt-1">{hint}</div>}
  </label>
);

const CategoriesPanel = () => {
  const { password } = useAdminAuth();
  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null); // null or {id, display, label, sort_order, isNew}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await axios.get(`${API}/admin/categories`, { headers });
      setCats(data);
    } catch { setErr('Failed to load categories.'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cats;
    return cats.filter(c => (c.id + ' ' + (c.display || '')).toLowerCase().includes(q));
  }, [cats, query]);

  const upd = (k, v) => setEditing(prev => ({ ...prev, [k]: v }));

  const startNew = () => setEditing({ ...emptyForm, isNew: true });
  const startEdit = (c) => setEditing({ id: c.id, display: c.display || '', label: c.label || '', sort_order: c.sort_order ?? '', isNew: false, _origId: c.id });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    setSaving(true); setErr('');
    try {
      const payload = {
        display: editing.display?.trim(),
        label: (editing.label || '').trim() || undefined,
        sort_order: editing.sort_order === '' ? undefined : Number(editing.sort_order),
      };
      if (editing.isNew) {
        payload.id = editing.id?.trim().toLowerCase();
        await axios.post(`${API}/admin/categories`, payload, { headers });
      } else {
        await axios.patch(`${API}/admin/categories/${editing._origId}`, payload, { headers });
      }
      setEditing(null);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save category');
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (c.count > 0) {
      window.alert(`Cannot delete "${c.display}" — ${c.count} product(s) still use it. Move or delete those products first.`);
      return;
    }
    if (!window.confirm(`Delete category "${c.display}"?`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${c.id}`, { headers });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to delete category');
    }
  };

  const move = async (c, direction) => {
    const idx = cats.findIndex(x => x.id === c.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= cats.length) return;
    const other = cats[swapIdx];
    try {
      await Promise.all([
        axios.patch(`${API}/admin/categories/${c.id}`, { sort_order: other.sort_order }, { headers }),
        axios.patch(`${API}/admin/categories/${other.id}`, { sort_order: c.sort_order }, { headers }),
      ]);
      await load();
    } catch { setErr('Failed to reorder'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-[24px] text-neutral-900 dark:text-neutral-100">Categories</h2>
          <p className="text-[12.5px] text-neutral-500">{cats.length} categories · powers the customer catalog filter</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search categories…"
            className="w-full bg-white dark:bg-[#141416] border border-neutral-200 dark:border-neutral-800 rounded-md pl-9 pr-3 py-2.5 text-[14px] outline-none focus:border-neutral-400"
          />
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-2.5 text-[13.5px] hover:bg-neutral-50 dark:hover:bg-neutral-800"><RefreshCw size={14} /> Refresh</button>
        <button data-testid="add-category-btn" onClick={startNew} className="inline-flex items-center gap-1.5 bg-[#1e293b] hover:bg-[#0f172a] text-white rounded-md px-4 py-2.5 text-[13.5px]"><Plus size={15} /> Add category</button>
      </div>

      {err && <div className="text-red-600 text-[13px]">{err}</div>}

      <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-2.5 text-[10.5px] uppercase tracking-widest text-neutral-500 border-b border-neutral-200 dark:border-neutral-800">
          <div className="col-span-1">Order</div>
          <div className="col-span-3">ID</div>
          <div className="col-span-3">Display</div>
          <div className="col-span-3">Label</div>
          <div className="col-span-1 text-right">Products</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-neutral-500 text-[13.5px]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center text-neutral-500 text-[13.5px]">No categories found.</div>
        ) : filtered.map((c, i) => (
          <div key={c.id} data-testid={`category-row-${c.id}`} className="grid grid-cols-12 gap-3 px-4 py-3 items-center border-b border-neutral-100 dark:border-neutral-800 last:border-b-0 hover:bg-neutral-50/60 dark:hover:bg-neutral-900/40">
            <div className="col-span-1 flex items-center gap-1">
              <span className="text-[12px] text-neutral-500 w-5">{c.sort_order}</span>
              <button aria-label="Move up" title="Move up" onClick={() => move(c, 'up')} className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500"><ArrowUp size={12} /></button>
              <button aria-label="Move down" title="Move down" onClick={() => move(c, 'down')} className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500"><ArrowDown size={12} /></button>
            </div>
            <div className="col-span-3 font-mono text-[12.5px] text-neutral-700 dark:text-neutral-300 flex items-center gap-1.5"><Folder size={12} className="text-neutral-400" />{c.id}</div>
            <div className="col-span-3 text-[13.5px] text-neutral-900 dark:text-neutral-100 truncate">{c.display}</div>
            <div className="col-span-3 text-[11.5px] font-mono text-neutral-500 truncate">{c.label}</div>
            <div className="col-span-1 text-right text-[13px] text-neutral-700 dark:text-neutral-200">{c.count}</div>
            <div className="col-span-1 flex items-center justify-end gap-1">
              <button data-testid={`edit-category-${c.id}`} onClick={() => startEdit(c)} className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-300" title="Edit"><Pencil size={13} /></button>
              <button data-testid={`delete-category-${c.id}`} onClick={() => remove(c)} disabled={c.count > 0} title={c.count > 0 ? `${c.count} products use this` : 'Delete'} className={`p-1.5 rounded-md ${c.count > 0 ? 'text-neutral-300 dark:text-neutral-700 cursor-not-allowed' : 'text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-red-500'}`}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto" onClick={cancel}>
          <div data-testid="category-form-modal" className="mt-16 w-full max-w-lg bg-white dark:bg-[#141416] rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
              <h3 className="font-serif text-[18px] text-neutral-900 dark:text-neutral-100">{editing.isNew ? 'Add category' : 'Edit category'}</h3>
              <button onClick={cancel} className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <FormRow label="ID" hint="Lowercase, letters/numbers/dashes only. Cannot be changed once set.">
                <input
                  data-testid="category-id-input"
                  className={inputCls + (editing.isNew ? '' : ' opacity-60 cursor-not-allowed')}
                  disabled={!editing.isNew}
                  value={editing.id}
                  onChange={(e) => upd('id', slugify(e.target.value))}
                  onBlur={() => { if (editing.isNew && !editing.id && editing.display) upd('id', slugify(editing.display)); }}
                  placeholder="e.g. supplements"
                />
              </FormRow>
              <FormRow label="Display name" hint="Shown on the customer catalog pills.">
                <input
                  data-testid="category-display-input"
                  className={inputCls}
                  value={editing.display}
                  onChange={(e) => upd('display', e.target.value)}
                  onBlur={(e) => {
                    if (editing.isNew && !editing.id && e.target.value) upd('id', slugify(e.target.value));
                  }}
                  placeholder="e.g. Supplements"
                />
              </FormRow>
              <FormRow label="Label (optional)" hint="Uppercase tagline on product cards. Auto-generated from display name if left blank.">
                <input
                  data-testid="category-label-input"
                  className={inputCls}
                  value={editing.label}
                  onChange={(e) => upd('label', e.target.value)}
                  placeholder="Defaults to display in uppercase"
                />
              </FormRow>
              {!editing.isNew && (
                <FormRow label="Sort order">
                  <input type="number" className={inputCls} value={editing.sort_order} onChange={(e) => upd('sort_order', e.target.value)} />
                </FormRow>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-neutral-200 dark:border-neutral-800">
              <button onClick={cancel} className="px-4 py-2 rounded-md border border-neutral-200 dark:border-neutral-700 text-[13.5px] text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800">Cancel</button>
              <button
                data-testid="save-category-btn"
                onClick={save}
                disabled={saving || !editing.display.trim() || (editing.isNew && !editing.id.trim())}
                className="inline-flex items-center gap-1.5 bg-[#1e293b] hover:bg-[#0f172a] text-white rounded-md px-4 py-2 text-[13.5px] disabled:opacity-60"
              ><Save size={14} /> {saving ? 'Saving…' : (editing.isNew ? 'Create' : 'Save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriesPanel;
